/**
 * Окно цены товара договора — клик по цене во вкладке «Договор» (право `contract_edit`, **пишет в боевую
 * CRM**, затем синхронизация и сверка сделки Bitrix). Как `AddChangeOrderProdDialog.vue` CRM (владелец
 * 23.10.26): вид «Прокат / Продажа», дней проката 1 / 2 / 3, рекомендованная цена из прайса серии
 * (`sPrice`: прокат 1/2/3 дня и минимум, продажа и минимум) кликом или своя цена руками. Пока цену не
 * вводили руками, смена вида и дней подставляет цену прайса. Ниже минимума — предупреждение, не запрет
 * (CRM тоже не запрещает). Уходят только изменённые поля `priceAccount` / `priceDay` / `isSale`.
 */
import { useMutation } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

import type { RowEditCtx } from "@/components/contract/RowEdit";
import { numCls } from "@/components/contract/ui";
import { type RowPrices, PRICE_MAX, patchContractRow, useContractRefresh, writeError } from "@/lib/contract";
import { btn } from "@/lib/panelBtn";

const rub = (n: number) => `${n.toLocaleString("ru-RU")} ₽`;
const DAYS = [1, 2, 3] as const;
const STATUS_TEXT: Record<number, string> = { 2: "запрет", 3: "уточнить у владельца" };

type Offer = { key: string; label: string; price: number; days?: number };

/** Рекомендованные цены вида: прокат — 1/2/3 дня и минимум; продажа — цена и минимум. */
function offers(p: RowPrices | undefined, sale: boolean): Offer[] {
  if (!p) return [];
  if (sale) {
    if (p.sale_status !== 1) return [];
    return [
      ...(p.sale ? [{ key: "sale", label: "Продажа", price: p.sale }] : []),
      ...(p.sale_min ? [{ key: "sale_min", label: "миним.", price: p.sale_min }] : []),
    ];
  }
  if (p.rent_status !== 1) return [];
  const out: Offer[] = [];
  DAYS.forEach((d, i) => {
    const v = p.rent[i];
    if (v) out.push({ key: `rent${d}`, label: d === 1 ? "1 день" : `${d} дня`, price: v, days: d });
  });
  if (p.rent_min) out.push({ key: "rent_min", label: "миним.", price: p.rent_min, days: 1 });
  return out;
}

/** Цена прайса для вида и дней: прокат — за N дней, продажа — цена продажи. */
const reference = (p: RowPrices | undefined, sale: boolean, days: number) =>
  p ? (sale ? (p.sale_status === 1 ? p.sale : null) : p.rent_status === 1 ? (p.rent[days - 1] ?? null) : null) : null;

export function PriceDialog({ ctx, prices, title, onClose }: { ctx: RowEditCtx; prices: RowPrices | undefined; title: string; onClose: () => void }) {
  // Снимок строки на открытии: «изменено» — только то, что тронул сотрудник.
  const [row] = useState(() => ctx.row);
  const service = ctx.service || row.article === 700700;
  const podzakaz = Boolean(row.podzakazID);
  const refresh = useContractRefresh(ctx.dealId, ctx.dogovorId, ctx.chatId);
  const [sale, setSale] = useState(Boolean(row.isSale));
  const [days, setDays] = useState<number | null>(row.priceDay && row.priceDay >= 1 && row.priceDay <= 3 ? row.priceDay : null);
  const [price, setPrice] = useState(row.priceAccount == null ? "" : String(row.priceAccount));
  const [manual, setManual] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !e.defaultPrevented && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /** Сменили вид или дни, а цену руками не трогали — цена прайса, если она есть. */
  const autofill = (nextSale: boolean, nextDays: number | null) => {
    if (manual) return;
    const v = reference(prices, nextSale, nextDays ?? 1);
    if (v) {
      setPrice(String(v));
      setPicked(null);
    }
  };
  const pick = (o: Offer) => {
    setPrice(String(o.price));
    if (o.days && !sale) setDays(o.days);
    setPicked(o.key);
    setManual(false);
  };

  const t = price.trim();
  const p = /^\d+$/.test(t) && Number(t) <= PRICE_MAX ? Number(t) : undefined;
  const changed: Record<string, unknown> = {};
  if (p !== undefined && p !== row.priceAccount) changed.priceAccount = p;
  if (!service) {
    if (!podzakaz && Number(sale) !== row.isSale) changed.isSale = Number(sale);
    if (!sale && days != null && days !== row.priceDay) changed.priceDay = days;
  }
  const empty = Object.keys(changed).length === 0;
  const min = sale ? prices?.sale_min : prices?.rent_min;
  const below = !service && p !== undefined && min != null && min > 0 && p < min;
  const status = sale ? prices?.sale_status : prices?.rent_status;
  const list = service ? [] : offers(prices, sale);

  const save = useMutation({
    mutationFn: () => patchContractRow(ctx.dogovorId, row.id, changed, ctx.version, ctx.chatId),
    onSuccess: (r) => (ctx.onDone("Цена записана в CRM", r), onClose()),
    onSettled: refresh,
  });
  const ready = !empty && p !== undefined && !save.isPending;

  const seg = (on: boolean) =>
    `h-8 border px-3 first:rounded-l-[var(--radius-ui)] last:rounded-r-[var(--radius-ui)] disabled:cursor-not-allowed disabled:opacity-50 ${
      on ? "border-accent bg-accent text-white" : "border-border hover:bg-bg-hover"
    }`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3" onClick={() => !save.isPending && onClose()}>
      <form
        className="flex max-h-full w-full max-w-[420px] flex-col gap-3 overflow-y-auto rounded-[16px] bg-bg p-4 text-[13px] text-fg"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => (e.preventDefault(), ready && save.mutate())}
        data-annot={`цена строки договора ${row.id}`}
      >
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-medium">Цена</div>
            <div className="break-words text-fg-muted">{title}</div>
            <div className="text-[12px] text-fg-muted">
              сейчас {rub(row.priceAccount ?? 0)}
              {!service && (row.isSale ? " · продажа" : row.priceDay ? ` · прокат ${row.priceDay} дн.` : " · прокат")}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-fg-muted hover:text-fg" title="Закрыть (Esc)">
            <X size={18} aria-hidden />
          </button>
        </div>
        <fieldset disabled={save.isPending} className="flex flex-col gap-3">
          {!service && (
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-fg-muted">Вид</span>
                <div className="flex" role="radiogroup" aria-label="Прокат или продажа" title={podzakaz ? "У подзаказа вид не меняют" : undefined}>
                  {[false, true].map((v) => (
                    <button
                      key={String(v)}
                      type="button"
                      role="radio"
                      aria-checked={sale === v}
                      disabled={podzakaz}
                      onClick={() => (setSale(v), autofill(v, days))}
                      className={seg(sale === v)}
                    >
                      {v ? "Продажа" : "Прокат"}
                    </button>
                  ))}
                </div>
              </div>
              {!sale && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-fg-muted">Цена за</span>
                  <div className="flex" role="radiogroup" aria-label="Дней проката">
                    {DAYS.map((d) => (
                      <button key={d} type="button" role="radio" aria-checked={days === d} onClick={() => (setDays(d), autofill(false, d))} className={seg(days === d)}>
                        {d} {d === 1 ? "день" : "дня"}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {!service && (
            <div className="flex flex-col gap-1" data-annot="рекомендованные цены">
              <span className="text-fg-muted">Рекомендованная цена</span>
              {!prices ? (
                <span className="text-[12px] text-fg-faint">У серии нет прайса — цену вводят руками.</span>
              ) : status !== 1 ? (
                <span className="text-[12px] text-warn">{sale ? "Продажа" : "Прокат"}: {STATUS_TEXT[status ?? 0] ?? "нет данных"}</span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {list.map((o) => {
                    const on = picked === o.key || (!picked && p === o.price && (!o.days || sale || days === o.days));
                    return (
                      <button
                        key={o.key}
                        type="button"
                        onClick={() => pick(o)}
                        className={`flex flex-col items-start rounded-[var(--radius-ui)] border px-2.5 py-1 text-left ${on ? "border-accent bg-accent/10" : "border-border hover:bg-bg-hover"}`}
                      >
                        <span className="text-[11.5px] text-fg-muted">{o.label}</span>
                        <span className="font-medium tabular-nums">{rub(o.price)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <label className="flex flex-col gap-0.5">
            <span className="text-fg-muted">Цена для клиента, ₽ {service ? "" : "— или своя"}</span>
            <input
              className={`${numCls} w-36`}
              inputMode="numeric"
              value={price}
              autoFocus={service}
              onChange={(e) => (setPrice(e.target.value), setManual(true), setPicked(null))}
            />
          </label>
          {p === undefined && <div className="text-err">Цена — целое число рублей</div>}
          {below && <div className="text-warn">Ниже минимальной ({rub(min!)}) — проверьте, что так и задумано</div>}
        </fieldset>
        {save.isError && <div className="whitespace-pre-wrap text-err">{writeError(save.error)}</div>}
        <div className="flex flex-wrap items-center gap-2">
          <button type="submit" className={btn.primary} disabled={!ready}>
            {save.isPending ? "Записываю и сверяю с Bitrix…" : "Сохранить"}
          </button>
          <button type="button" className={btn.quiet} onClick={onClose} disabled={save.isPending}>
            Отмена
          </button>
          {empty && p !== undefined && <span className="text-[11.5px] text-fg-muted">Изменений нет</span>}
        </div>
      </form>
    </div>
  );
}
